import * as React from "react";
import { Namespace, useT } from "@racoon/i18n";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { Button, Modal, ModalContent, useToast } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { useGetMeQuery } from "~/shared/api/auth/auth.grpc";
import { type RoleKey } from "~/shared/api/user/user.grpc";
import { type ListNavigation } from "~/shared/hooks/use-list-navigation";
import { useUserForm, type UserFormOrg } from "../model/use-user-form";
import { UserFields } from "./user-fields";
import { ShiftHistoryTab } from "./shift-history.tab";

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** null = create mode, a user = edit mode */
  user: ConnectProto.User | null;
  /** The list refetches itself off the mutation's cache tags; use this for UI state only. */
  onSaved?: () => void;
  org: UserFormOrg;
  defaultRole?: RoleKey;
  /** Limit the role picker to the roles a provider staffs its own org with. */
  staffRolesOnly?: boolean;
  showGroups?: boolean;
  /** Prev/next through the surrounding list, edit mode only. */
  navigation?: ListNavigation;
}

type CardTab = "info" | "shifts" | "fines";

export function UserFormDialog({
  open,
  onOpenChange,
  user,
  onSaved,
  org,
  defaultRole,
  staffRolesOnly,
  showGroups,
  navigation,
}: UserFormDialogProps) {
  const t = useT();
  const { toast } = useToast();
  // Already in the RTK cache app-wide, so this adds no request.
  const { data: me } = useGetMeQuery({});
  const [activeTab, setActiveTab] = React.useState<CardTab>("info");

  const form = useUserForm({
    open,
    user,
    org,
    defaultRole,
    staffRolesOnly,
    onClose: () => onOpenChange(false),
    onSaved,
    onEmailRequired: () =>
      toast({ title: t(Namespace.Employees, "error.emailRequired"), variant: "destructive" }),
  });

  React.useEffect(() => {
    if (open) setActiveTab("info");
  }, [open, user]);

  // Shift history and fines are provider-scoped; a platform admin edits the
  // account itself, so the card collapses to the plain form.
  const showTabs = form.isEdit && me?.user?.role !== ConnectProto.UserRole.ADMIN;
  const userFullName = user ? [user.firstName, user.lastName].filter(Boolean).join(" ") : "";

  const tabs: { key: CardTab; label: string }[] = [
    { key: "info", label: t(Namespace.Employees, "card.tab.info") },
    { key: "shifts", label: t(Namespace.Employees, "card.tab.shiftHistory") },
    { key: "fines", label: t(Namespace.Employees, "card.tab.fines") },
  ];

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      {/* Same body either way; the card adds the 168px tab rail, so the plain
          form is that much narrower and the fields keep identical geometry. */}
      <ModalContent
        size="custom"
        className={
          showTabs
            ? "md:!w-[1200px] !p-0 !gap-0 overflow-hidden bg-[#f1f5f9]"
            : "md:!w-[1032px] !p-0 !gap-0 overflow-hidden"
        }
        showClose={false}
      >
        <div className="h-[57px] shrink-0 bg-white flex items-center justify-between px-4">
          <div className="flex items-center gap-3 min-w-0">
            {form.isEdit && navigation && (
              <div className="flex items-center gap-3 shrink-0">
                <button
                  type="button"
                  aria-label={t(Namespace.Employees, "nav.next")}
                  onClick={() => navigation.onNavigate(1)}
                  disabled={!navigation.canNext}
                  className="text-[#1d1f23] hover:opacity-60 transition-opacity disabled:opacity-30"
                >
                  <ChevronDown className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  aria-label={t(Namespace.Employees, "nav.prev")}
                  onClick={() => navigation.onNavigate(-1)}
                  disabled={!navigation.canPrev}
                  className="text-[#1d1f23] hover:opacity-60 transition-opacity disabled:opacity-30"
                >
                  <ChevronUp className="w-5 h-5" />
                </button>
              </div>
            )}
            <div className="min-w-0">
              {form.isEdit ? (
                <>
                  <div className="text-[15px] font-bold text-[#1d1f23] leading-tight truncate">
                    {userFullName}
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#64748b] leading-tight mt-0.5">
                    {t(Namespace.Employees, "card.title")}
                  </div>
                </>
              ) : (
                <div className="text-[15px] font-bold text-[#1d1f23]">
                  {t(Namespace.Employees, "create.title")}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            aria-label={t(Namespace.Common, "close")}
            onClick={() => onOpenChange(false)}
            className="w-6 h-6 rounded-full bg-[#1d1f23] hover:opacity-80 transition-opacity flex items-center justify-center shrink-0"
          >
            <X className="w-3.5 h-3.5 text-white" />
          </button>
        </div>

        <div className="h-px bg-[#e2e8f0] shrink-0" />

        <div className="flex flex-1 min-h-0">
          {showTabs && (
            <div className="w-[168px] shrink-0 bg-[#f1f5f9] border-r border-[#e2e8f0] px-2 pt-3 flex flex-col gap-1">
              {tabs.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={
                    activeTab === key
                      ? "h-8 px-3 rounded-lg bg-brand-blue text-white text-sm font-medium text-left"
                      : "h-8 px-3 rounded-lg text-[#1d1f23] text-sm text-left hover:bg-white/70 transition-colors"
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {activeTab === "shifts" && user ? (
            <div className="flex-1 min-w-0 bg-white flex">
              <ShiftHistoryTab user={user} />
            </div>
          ) : activeTab === "fines" ? (
            <div className="flex-1 min-w-0 bg-white flex items-center justify-center text-sm text-[#64748b]">
              {t(Namespace.Employees, "card.empty")}
            </div>
          ) : (
            <div className="flex-1 min-w-0 bg-white overflow-y-auto">
              <UserFields form={form} org={org} user={user} showGroups={showGroups} />
            </div>
          )}
        </div>

        {activeTab === "info" && (
          <>
            <div className="h-px bg-[#e2e8f0] shrink-0" />
            <div className="h-[65px] shrink-0 bg-white flex items-center justify-end px-4">
              {form.isEdit && (
                <Button
                  id="user-dialog-archive-button"
                  variant={form.isArchived ? "solidBlue" : "solidRed"}
                  size="sm"
                  className="mr-auto"
                  disabled={form.isSubmitting}
                  onClick={form.toggleArchived}
                >
                  {form.isArchived
                    ? t(Namespace.Employees, "restore.button")
                    : t(Namespace.Employees, "archive.button")}
                </Button>
              )}
              <button
                id="user-dialog-submit-button"
                type="button"
                onClick={form.submit}
                disabled={!form.canSubmit || form.isSubmitting}
                className="h-8 px-4 rounded-lg bg-brand-blue text-white text-sm font-medium hover:bg-[#0066dd] transition-colors disabled:opacity-40"
              >
                {form.isEdit ? t(Namespace.Common, "save") : t(Namespace.Common, "create")}
              </button>
            </div>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
